import type { BordereauSettings, BordereauType } from './types';

/**
 * Reporting endpoints. Read-only aggregation views live under their own base so
 * they can be served by a warehouse replica rather than the transactional PAS
 * database. Paths sit behind `VITE_API_BASE_URL` like every other service.
 */
const REPORTING_BASE = '/api/reporting';

export const REPORTING_ENDPOINTS = {
  /** One flat row per policy transaction, filtered server-side. */
  POLICY_RECORDS: `${REPORTING_BASE}/policy-records/`,
  /** Filter option lists (products, carriers, brokers). */
  REFERENCE: `${REPORTING_BASE}/reference/`,
  /** Binding-authority defaults for the bordereau header fields. */
  BORDEREAU_SETTINGS: `${REPORTING_BASE}/bordereau-settings/`,
  /** Server-side CSV render, used when an export is too large for the browser. */
  BORDEREAU_EXPORT: `${REPORTING_BASE}/bordereaux/export/`,
} as const;

export interface ReportingNavItem {
  key: string;
  label: string;
  to: string;
  icon: string;
  end?: boolean;
}

export const REPORTING_NAV: ReportingNavItem[] = [
  { key: 'analytics', label: 'Analytics', to: '/reporting', icon: 'analytics', end: true },
  { key: 'bordereaux', label: 'Bordereaux', to: '/reporting/bordereaux', icon: 'bordereaux' },
];

export const BORDEREAU_TYPE_META: Record<BordereauType, { label: string; description: string }> = {
  risk: {
    label: 'Risk',
    description: 'Risk detail only — insured, location, period and limits. No financials.',
  },
  premium: {
    label: 'Premium',
    description: 'Premium, deductions and tax per certificate, with the identifiers to match it.',
  },
  combined: {
    label: 'Combined risk & premium',
    description: 'Every column in one file. The usual monthly submission for a binder.',
  },
};

/**
 * Lloyd's risk codes by product. These are the placeholder mappings used for the
 * demo — the binding authority contract dictates the real code per section, so
 * they must be confirmed against the slip before a live submission.
 */
export const PRODUCT_RISK_CODES: Record<string, { riskCode: string; classOfBusiness: string }> = {
  'Paid Family & Medical Leave': { riskCode: '1A', classOfBusiness: 'Accident & Health' },
  'Parental Leave Top-Up': { riskCode: '1A', classOfBusiness: 'Accident & Health' },
  'Short-Term Disability': { riskCode: '1A', classOfBusiness: 'Accident & Health' },
  'Supplemental Caregiver Leave': { riskCode: '1A', classOfBusiness: 'Accident & Health' },
};

export const DEFAULT_RISK_CODE = '1A';
export const DEFAULT_CLASS_OF_BUSINESS = 'Accident & Health';

/** ISO 4217 codes offered for the original/settlement currency fields. */
export const CURRENCY_OPTIONS = ['USD', 'GBP', 'EUR', 'CAD'];

/** Rows rendered in the on-screen bordereau preview; the CSV always contains every row. */
export const PREVIEW_ROW_LIMIT = 25;

/** Distinct values shown per validation issue before the list is truncated. */
export const VALIDATION_SAMPLE_LIMIT = 3;

export const DEFAULT_BORDEREAU_SETTINGS: BordereauSettings = {
  umr: 'B6118PARENTO26001',
  brokerReference: 'PAR-BA-2026-01',
  coverholderName: 'Parento Insurance Services LLC',
  coverholderPin: 'CH-0114872',
  binderReference: 'BA26PFML001',
  yearOfAccount: 2026,
  riskCode: DEFAULT_RISK_CODE,
  classOfBusiness: DEFAULT_CLASS_OF_BUSINESS,
  section: '1',
  originalCurrency: 'USD',
  settlementCurrency: 'USD',
  rateOfExchange: 1,
  periodEnd: '',
  includeBom: true,
};
